import { useState } from 'react';
import _ from 'lodash';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import { useOpenAi } from '@/hooks/useOpenAi';
import useProfile from '@/hooks/useProfileV2';
import interviewsServices, {
  AddInterviewReview,
  DeleteInterviewReview,
  UpdateInterviewReview,
} from '@/services/interviews';
import { InterviewReviewType, InterviewScore, InterviewType } from '@/types/api';
import {
  InterviewTypes,
  PRE_SCREENING_SCORE_INPUTS,
  SCREENING_SCORE_INPUTS,
  TEAM_INTERVIEW_SCORE_INPUTS,
} from '@/utils/interviews';
import InterviewReviewsManagerComponent, {
  CustomReviewType,
} from './InterviewReviewsManager.component';
import AiOverviewModal from '../AiOverviewModal';

type InterviewReviewsManagerProps = {
  interview: InterviewType;
  canAddNewReview?: boolean;
  onRefetchInterview: () => void;
};

export default function InterviewReviewsManager({
  interview,
  canAddNewReview = true,
  onRefetchInterview,
}: InterviewReviewsManagerProps): JSX.Element {
  const { profile } = useProfile();
  const toast = useToastContext();
  const { generatePreScreeningReview } = useOpenAi();
  const [isLoading, setIsLoading] = useState(false);

  const addInterviewReviewMutation = useApiSubmit<any, AddInterviewReview>(
    interviewsServices.addReviewInterview
  );

  const deleteInterviewReviewMutation = useApiSubmit<any, DeleteInterviewReview>(
    interviewsServices.deleteReviewInterview
  );

  const updateInterviewReviewMutation = useApiSubmit<any, UpdateInterviewReview>(
    interviewsServices.updateReviewInterview
  );

  const updateAiOverviewMutation = useApiSubmit<any, UpdateInterviewReview>(
    interviewsServices.updateReviewAiOverview
  );

  const defaultScores: Record<string, InterviewScore[]> = {
    [InterviewTypes.PRE_SCREENING]: PRE_SCREENING_SCORE_INPUTS,
    [InterviewTypes.SCREENING]: SCREENING_SCORE_INPUTS,
    [InterviewTypes.TEAM_INTERVIEW]: TEAM_INTERVIEW_SCORE_INPUTS,
  };

  const [reviews, setReviews] = useState<CustomReviewType[]>(
    interview.reviews.map((review) => ({
      ...review,
      score: review.score?.length ? review.score : (defaultScores[interview.type] ?? PRE_SCREENING_SCORE_INPUTS),
      isEditing: false,
    }))
  );

  const [aiOverviewModalState, setAiOverviewModalState] = useState<{
    isOpen: boolean;
    reviewId: string;
    overview: string;
  } | null>(null);

  const getDefaultScoreInputs = (type: InterviewTypes): InterviewScore[] => {
    switch (type) {
      case InterviewTypes.PRE_SCREENING:
        return PRE_SCREENING_SCORE_INPUTS;
      case InterviewTypes.SCREENING:
        return SCREENING_SCORE_INPUTS;
      case InterviewTypes.TEAM_INTERVIEW:
        return TEAM_INTERVIEW_SCORE_INPUTS;
      default:
        return PRE_SCREENING_SCORE_INPUTS;
    }
  };

  const shouldPromptAiOverviewModal = interview.type === InterviewTypes.TEAM_INTERVIEW;

  const onAddNewReview = (): void => {
    setReviews((prev) =>
      prev.concat({
        note: '',
        score: getDefaultScoreInputs(interview.type),
        user: { ...profile },
        isEditing: true,
      })
    );
  };

  const updateReviewAfterSubmit = (
    index: number,
    persistedNewReview: InterviewReviewType
  ): void => {
    setReviews((prev) =>
      prev.map((review, reviewIndex) => {
        if (index !== reviewIndex) {
          return review;
        }

        return {
          ..._.omit(persistedNewReview, ['interview']),
          isEditing: false,
        };
      })
    );
  };

  const maybeOpenAiOverviewModal = (persistedReview: InterviewReviewType): void => {
    if (!shouldPromptAiOverviewModal) return;
    if (!persistedReview.aiGeneratedOverview) return;

    setAiOverviewModalState({
      isOpen: true,
      reviewId: persistedReview.id,
      overview: persistedReview.aiGeneratedOverview,
    });
  };

  const onCancelAddNewInterviewReview = (interviewReviewIndex: number): void => {
    setReviews((prev) =>
      prev.filter((review, index) => {
        return interviewReviewIndex !== index;
      })
    );
  };

  const onAddNewInterviewReview = async (
    index: number,
    newReview: CustomReviewType
  ): Promise<boolean> => {
    const { id } = newReview;
    const isAlreadyCreated = !!id;
    const actionsVerb = isAlreadyCreated ? 'updated' : 'added';

    const methodToCall = isAlreadyCreated
      ? updateInterviewReviewMutation
      : addInterviewReviewMutation;

    try {
      const reviewWithOmittedFields = _.omit(newReview, [
        'isEditing',
        !isAlreadyCreated ? 'id' : '',
      ]);

      const persistedReview = (await methodToCall.mutateAsync({
        interviewId: interview.id,
        interviewReviewId: id,
        ...reviewWithOmittedFields,
      })) as InterviewReviewType;

      updateReviewAfterSubmit(index, persistedReview);
      onRefetchInterview();
      toast.showSuccessFeedback(`The review has been ${actionsVerb} to the interview.`);
      maybeOpenAiOverviewModal(persistedReview);
    } catch {
      const actionContinuousVerb = actionsVerb.replace('ed', 'ing');

      toast.showErrorFeedback(
        `There was a problem to ${actionContinuousVerb} the review to the interview.`
      );
    }

    return methodToCall.isSuccess;
  };

  const onGenerateReviewWithAI = async (
    interviewReviewIndex: number,
    newReview: CustomReviewType
  ): Promise<boolean> => {
    setIsLoading(true);
    const generatedAIReview = await generatePreScreeningReview(
      interview.questions.map((question) => ({
        questionText: question.questionText,
        audioUrl: question.answer?.attachmentUrl as string,
      }))
    );

    const parsedAIReview = JSON.parse(generatedAIReview);

    const updatedReview = {
      ...newReview,
      score: newReview.score?.map((existingScore) => {
        const aiScore = parsedAIReview.find(
          (score: { scoreType: string }) => score.scoreType === existingScore.scoreType
        );
        return {
          ...existingScore,
          score: aiScore?.score || existingScore.score,
        };
      }),
      note: 'Generated via AI',
    };

    const response = onAddNewInterviewReview(interviewReviewIndex, updatedReview);

    setIsLoading(false);
    return response;
  };

  const onDeleteInterviewReview = async (interviewReviewId: string): Promise<boolean> => {
    try {
      await deleteInterviewReviewMutation.mutateAsync({
        interviewId: interview.id,
        interviewReviewId,
      });

      setReviews((prev) =>
        prev.filter((review) => {
          return interviewReviewId !== review.id;
        })
      );
      onRefetchInterview();
      toast.showSuccessFeedback('The review has been deleted of the interview.');
    } catch {
      toast.showErrorFeedback('There was a problem to delete the review of the interview.');
    }

    return deleteInterviewReviewMutation.isSuccess;
  };

  const propertyToReview = interview.type === InterviewTypes.SCREENING ? 'notes' : 'answers';
  const emptyMessage = `There’re no ${propertyToReview} to be reviewed yet.`;

  const isActionLoading =
    addInterviewReviewMutation.isLoading ||
    updateInterviewReviewMutation.isLoading ||
    deleteInterviewReviewMutation.isLoading ||
    isLoading;

  return (
    <>
      <InterviewReviewsManagerComponent
        isActionLoading={isActionLoading}
        emptyMessage={emptyMessage}
        canAddNewReview
        reviews={reviews}
        reviewsResult={interview.reviewsResult}
        finalScore={interview.finalScore}
        interviewType={interview.type}
        onAddNewInterviewReview={onAddNewInterviewReview}
        onAddNewReview={onAddNewReview}
        onGenerateReviewWithAI={onGenerateReviewWithAI}
        onDeleteInterviewReview={onDeleteInterviewReview}
        onCancelAddNewInterviewReview={onCancelAddNewInterviewReview}
      />

      {aiOverviewModalState && (
        <AiOverviewModal
          isOpen={aiOverviewModalState.isOpen}
          initialOverview={aiOverviewModalState.overview}
          isSubmitting={updateAiOverviewMutation.isLoading}
          onSubmit={async (updatedOverview) => {
            try {
              await updateAiOverviewMutation.mutateAsync({
                interviewId: interview.id,
                interviewReviewId: aiOverviewModalState.reviewId,
                aiGeneratedOverview: updatedOverview,
              });
              onRefetchInterview();
              toast.showSuccessFeedback('The AI-generated overview has been updated.');
              setAiOverviewModalState(null);
            } catch {
              toast.showErrorFeedback('There was a problem updating the AI-generated overview.');
            }
          }}
        />
      )}
    </>
  );
}
